import React, { useState } from "react";

import type ComplexPromotionModel from "@admin/domain/complexPromotions/models/ComplexPromotionModel";
import { AmountCondition } from "@admin/domain/complexPromotions/models/Conditions";
import Select from "@admin/components/Select";
import Input from "@admin/components/Input";
import ProductsSearch from "@admin/components/ProductsSearch";
import CheckBox from "@admin/components/CheckBox";
import Rewards from "./Rewards";
import GiftOptions from "./GiftOptions";

type Levels = ComplexPromotionModel[`levels`];
type Level = Levels[0];
type EligibleProducts = ComplexPromotionModel[`eligible_products`];
type ExcludedProducts = ComplexPromotionModel[`excluded_products`];

type Props = {
    promotion: ComplexPromotionModel | null;
};

const ContentPromotion: React.FC<Props> = ({ promotion }) => {
    const [levels, setLevels] = useState<Levels>(promotion?.levels || []);
    const [eligibleProducts, setEligibleProducts] = useState<EligibleProducts>(promotion?.eligible_products || []);
    const [excludedProducts, setExcludedProducts] = useState<ExcludedProducts>(promotion?.excluded_products || []);
    const [condition, setCondition] = useState<AmountCondition>(
        promotion?.condition === AmountCondition.Money ? AmountCondition.Money : AmountCondition.Points,
    );

    const addLevel = (): void =>
        setLevels([
            ...levels,
            {
                amount_requirement: 0,
                content_requirements: [],
                promocode_requirements: [],
                rewards: [],
                gift_options: [],
            },
        ]);

    const deleteLevel = (levelIndex: number): void => {
        setLevels(levels.filter((_level: Level, index: number): boolean => levelIndex !== index));
    };

    const addEligibleProduct = (): void => setEligibleProducts([...eligibleProducts, {}]);

    const updateEligibleProduct = (
        productIndex: number,
        productId: string | null | undefined,
        productTitle: string | null | undefined,
    ): void => {
        setEligibleProducts(
            eligibleProducts.map((product, index) => {
                return productIndex === index
                    ? {
                          product_id: productId || undefined,
                          product_title: productTitle || undefined,
                      }
                    : product;
            }),
        );
    };

    const deleteEligibleProduct = (productIndex: number): void => {
        setEligibleProducts(eligibleProducts.filter((_product, index): boolean => productIndex !== index));
    };

    const addExcludedProduct = (): void => setExcludedProducts([...excludedProducts, {}]);

    const updateExcludedProduct = (
        productIndex: number,
        productId: string | null | undefined,
        productTitle: string | null | undefined,
    ): void => {
        setExcludedProducts(
            excludedProducts.map((product, index) => {
                return productIndex === index
                    ? {
                          product_id: productId || undefined,
                          product_title: productTitle || undefined,
                      }
                    : product;
            }),
        );
    };

    const deleteExcludedProduct = (productIndex: number): void => {
        setExcludedProducts(excludedProducts.filter((_product, index): boolean => productIndex !== index));
    };

    return (
        <>
            <Select
                label="Тип суммы"
                name="condition"
                value={condition}
                onChange={(event): void => setCondition(event.target.value as AmountCondition)}
                options={[
                    {
                        value: AmountCondition.Money,
                        label: `Цена товара (в р.)`,
                    },
                    { value: AmountCondition.Points, label: `Баллы` },
                ]}
                prependEmptyOption={false}
                required
            />

            <CheckBox
                name="show_progress_bar"
                label="Показывать прогресс акции у корзины"
                defaultChecked={promotion?.show_progress_bar ?? false}
            />

            <div className="rounded border p-10 my-5">
                <h2>Товары, участвующие в расчёте акции</h2>

                {eligibleProducts.map((product, productIndex) => (
                    <div
                        key={`${product.product_id || `new-eligible-product`}-${productIndex}`}
                        className="rounded border p-6 my-5"
                    >
                        <ProductsSearch
                            label="Товар"
                            inputName={`eligible_products[${productIndex}][product_id]`}
                            productId={product.product_id}
                            productTitle={product.product_title}
                            onChange={(productId, productTitle): void =>
                                updateEligibleProduct(productIndex, productId, productTitle)
                            }
                        />

                        <button
                            type="button"
                            className="btn btn-danger btn-sm"
                            onClick={(): void => deleteEligibleProduct(productIndex)}
                        >
                            Удалить
                        </button>
                    </div>
                ))}

                <button type="button" className="btn btn-primary" onClick={addEligibleProduct}>
                    Добавить товар
                </button>
            </div>

            <div className="rounded border p-10 my-5">
                <h2>Товары исключенные из акции</h2>

                {excludedProducts.map((product, productIndex) => (
                    <div
                        key={`${product.product_id || `new-excluded-product`}-${productIndex}`}
                        className="rounded border p-6 my-5"
                    >
                        <ProductsSearch
                            label="Товар"
                            inputName={`excluded_products[${productIndex}][product_id]`}
                            productId={product.product_id}
                            productTitle={product.product_title}
                            onChange={(productId, productTitle): void =>
                                updateExcludedProduct(productIndex, productId, productTitle)
                            }
                        />

                        <button
                            type="button"
                            className="btn btn-danger btn-sm"
                            onClick={(): void => deleteExcludedProduct(productIndex)}
                        >
                            Удалить
                        </button>
                    </div>
                ))}

                <button type="button" className="btn btn-primary" onClick={addExcludedProduct}>
                    Добавить товар
                </button>
            </div>

            <h2>Уровни акции</h2>

            {levels.map((level, levelIndex: number) => (
                <div key={level.id || `new-level-${levelIndex}`} className="rounded border p-10 my-5">
                    <input type="hidden" name={`levels[${levelIndex}][id]`} value={level.id} />

                    <Input
                        label="Сумма"
                        defaultValue={level.amount_requirement ? level.amount_requirement / 100 : undefined}
                        name={`levels[${levelIndex}][amount_requirement]`}
                    />

                    <Rewards levelIndex={levelIndex} level={level} allowEmpty />

                    <GiftOptions levelIndex={levelIndex} level={level} />

                    {levels.length > 1 && (
                        <button
                            type="button"
                            className="btn btn-danger"
                            onClick={() => {
                                if (confirm(`Удалить уровень?`)) {
                                    deleteLevel(levelIndex);
                                }
                            }}
                        >
                            Удалить уровень
                        </button>
                    )}
                </div>
            ))}

            <div className="rounded border p-10 my-5 cursor-pointer" onClick={addLevel}>
                <i className="fas fa-plus me-2" />
                Добавить уровень
            </div>
        </>
    );
};

export default ContentPromotion;
